/**
 * PostHog browser client — lazy singleton.
 *
 * Never import posthog-js at module top-level of pages/components; always
 * call `getPosthog()` (browser only). This keeps SSR bundles clean and
 * avoids loading the SDK when the kill-switch is off.
 *
 * Kill-switch: set NEXT_PUBLIC_POSTHOG_ENABLED=false to disable everything
 * (getPosthog() returns null, track() becomes a no-op).
 */

let _posthog = null;
let _initPromise = null;

function isLocalDevelopmentHost() {
  if (typeof window === "undefined") return false;
  const { hostname } = window.location;
  return (
    hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1"
  );
}

export function isPosthogEnabled() {
  if (typeof window === "undefined") return false;
  if (process.env.NEXT_PUBLIC_POSTHOG_ENABLED === "false") return false;
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return false;
  return true;
}

export function canRecordPosthogSession() {
  if (!isPosthogEnabled()) return false;
  if (process.env.NODE_ENV === "development") return false;
  if (isLocalDevelopmentHost()) return false;
  return true;
}

/**
 * Known harmless crash inside @cloudflare/realtimekit-ui: its stencil
 * web components read properties off `this.meeting` (e.g. `.stage`,
 * `.chat`) in disconnectedCallback/disconnectMeeting without a null
 * check. This fires when the meeting is torn down (leave/end) in the
 * same tick the RTK UI elements are removed from the DOM, so
 * `this.meeting` is already null by the time the teardown code runs.
 * It's cosmetic (the component is being unmounted anyway) but
 * posthog-js's `capture_exceptions` autocapture listens on window
 * "error"/"unhandledrejection" directly, so it bypasses the app-level
 * filtering in PostHogProvider. Drop it here too.
 *
 * Matches both V8 ("Cannot read properties of null (reading 'x')")
 * and JavaScriptCore/Safari ("null is not an object (evaluating
 * 'e.x')") style messages for a null/undefined property read.
 */
const NULL_PROPERTY_READ_RE =
  /cannot read propert(?:y|ies) of (?:null|undefined) \(reading '(\w+)'\)|(?:null|undefined) is not an object \(evaluating '[\w.]*\.(\w+)'\)/;

function isRealtimeKitStageTeardownNoise(exception) {
  const value = `${exception?.value || exception?.$exception_message || ""}`.toLowerCase();
  const match = value.match(NULL_PROPERTY_READ_RE);
  if (!match) return false;
  const property = match[1] || match[2];
  if (!["stage", "chat"].includes(property)) return false;

  const frames =
    exception?.stacktrace?.frames ||
    exception?.$exception_list?.[0]?.stacktrace?.frames ||
    [];
  const stackText = frames
    .map((f) => `${f?.filename || ""} ${f?.function || ""}`)
    .join(" ")
    .toLowerCase();
  const rawStack = `${exception?.stack || ""}`.toLowerCase();
  const haystack = `${stackText} ${rawStack}`;
  return (
    haystack.includes("realtimekit") ||
    haystack.includes("stagestatusupdate") ||
    haystack.includes("activejoinstage") ||
    haystack.includes("this.meeting.stage.join") ||
    haystack.includes("disconnectmeeting") ||
    haystack.includes("disconnectedcallback")
  );
}

/**
 * RealtimeKit polls WebRTC stats (getStats/getProducersReport) on an
 * interval that isn't cleared before the peer connection closes, so a
 * poll in flight during/after leave throws `InvalidStateError: closed`.
 * Same cosmetic-teardown-noise category as the check above.
 */
function isRealtimeKitStatsPollingNoise(exception) {
  const name = `${exception?.type || exception?.$exception_type || ""}`.toLowerCase();
  const value = `${exception?.value || exception?.$exception_message || ""}`.toLowerCase();
  const isClosedStateError =
    name.includes("invalidstateerror") ||
    (value.includes("invalidstateerror") && value.includes("closed")) ||
    value.trim() === "closed";
  if (!isClosedStateError) return false;

  const frames =
    exception?.stacktrace?.frames ||
    exception?.$exception_list?.[0]?.stacktrace?.frames ||
    [];
  const stackText = frames
    .map((f) => `${f?.filename || ""} ${f?.function || ""}`)
    .join(" ")
    .toLowerCase();
  const rawStack = `${exception?.stack || ""}`.toLowerCase();
  const haystack = `${stackText} ${rawStack}`;
  return (
    haystack.includes("realtimekit") ||
    haystack.includes("getproducersreport") ||
    haystack.includes("generateproducerstreammap") ||
    haystack.includes("getproducerstatsfromreport") ||
    haystack.includes("parsertcreport")
  );
}

function isIgnorablePosthogException(event) {
  if (event?.event !== "$exception") return false;
  const exceptionList =
    event?.properties?.$exception_list ||
    (event?.properties?.$exception_message
      ? [event.properties]
      : []);
  return exceptionList.some(
    (exception) =>
      isRealtimeKitStageTeardownNoise(exception) ||
      isRealtimeKitStatsPollingNoise(exception),
  );
}

/**
 * Initialize (idempotent). Returns the posthog instance or null.
 * Options are applied only on first call.
 */
export async function initPosthog() {
  if (!isPosthogEnabled()) return null;
  if (_posthog) return _posthog;
  if (_initPromise) return _initPromise;

  const posthogApiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || "/ingest";

  _initPromise = (async () => {
    const mod = await import("posthog-js");
    const posthog = mod.default || mod;

    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
      api_host: posthogApiHost,
      ui_host: "https://us.posthog.com",

      // App Router: we handle pageviews manually via <PageviewTracker/>
      capture_pageview: false,
      capture_pageleave: true,

      // Auto-capture uncaught exceptions & unhandled promise rejections
      // and forward them to PostHog's Error Tracking product.
      // Our <PostHogProvider/> also forwards console.error explicitly.
      capture_exceptions: true,

      autocapture: true,

      // Drop known-noisy third-party exceptions before they're queued.
      // capture_exceptions listens on window directly, so the filtering
      // done in PostHogProvider's own error handlers never runs for it.
      before_send: (event) =>
        isIgnorablePosthogException(event) ? null : event,

      // Replays are expensive and may include internal workflows. Start
      // disabled; IdentifyBridge starts them only for non-admin production users.
      disable_session_recording: true,
      session_recording: {
        maskAllInputs: false,
        recordCrossOriginIframes: true,
        recordCanvas: true,
        collectFonts: true,
      },

      // Heatmaps stay ON (needed by session recordings for the heatmap
      // overlay). Web-vitals OFF — saves ~4 events per pageview and we
      // already have Vercel Speed Insights covering Core Web Vitals.
      capture_heatmaps: true,
      capture_performance: false,

      // Persistence (survives across subdomains: hub.meex.co, meex.co)
      persistence: "localStorage+cookie",
      cross_subdomain_cookie: true,
      cookie_name: "ph_meex",
      secure_cookie: process.env.NODE_ENV === "production",
      // Respect Do-Not-Track browser header
      respect_dnt: true,

      // Environment tag — filter dev out of prod dashboards
      loaded: (ph) => {
        ph.register({
          app: "meexhub",
          environment:
            process.env.NEXT_PUBLIC_VERCEL_ENV ||
            process.env.NODE_ENV ||
            "development",
          release: process.env.NEXT_PUBLIC_APP_VERSION || "unknown",
        });
      },

      // Bootstrap advanced defaults
      advanced_disable_flags: false,
      advanced_disable_toolbar_metrics: true,

      // Retry on failure (offline support)
      request_batching: true,

      // Debug in dev only
      debug: process.env.NODE_ENV === "development",
    });

    _posthog = posthog;
    return posthog;
  })();

  return _initPromise;
}

/**
 * Synchronous accessor — returns the client if already initialized, else null.
 */
export function getPosthog() {
  if (!isPosthogEnabled()) return null;
  return _posthog;
}
